import JSZip from 'jszip';
import { Node, Edge } from 'reactflow';

interface BotpressNode {
  id: string;
  name: string;
  type: string;
  x: number;
  y: number;
  instructions?: any[];
  defaultTransition?: { targetNodeId: string };
  transitions?: Array<{ target: string; condition?: string; label?: string }>;
  next?: Array<{ node: string; condition?: string }>;
  comment?: string;
}

interface BotpressFlow {
  id: string;
  name: string;
  startNode: string;
  nodes: BotpressNode[];
  links: Array<{
    id: string;
    source: string;
    target: string;
    sourcePort?: string;
  }>;
  variables?: any[];
}

interface BotpressBot {
  version: string;
  settings: any;
  flows: BotpressFlow[];
}

/**
 * Mappa un tipo di nodo Botpress a un tipo PIGNA Flow
 */
function mapNodeType(bpNode: BotpressNode): string {
  const type = bpNode.type.toLowerCase();
  
  // Entry = Start
  if (type === 'entry') return 'start';
  
  // Standard con istruzioni
  if (type === 'standard') {
    if (!bpNode.instructions || bpNode.instructions.length === 0) {
      return 'action';
    }
    
    const firstInstr = bpNode.instructions[0];
    if (firstInstr.type === 'content') return 'say';
    if (firstInstr.type === 'transition') return 'condition';
    if (firstInstr.type === 'listen') return 'input';
    if (firstInstr.type === 'kb_answer') return 'rag';
    
    return 'action';
  }
  
  // Comment = ignorato per ora
  if (type === 'comment') return 'action'; // o potremmo saltarlo
  
  // Altri tipi
  if (type === 'send_message') return 'say';
  if (type === 'listen') return 'input';
  if (type === 'kb_answer' || type === 'knowledge') return 'rag';
  if (type === 'router' || type === 'condition') return 'condition';
  if (type === 'end' || type === 'success') return 'end';
  
  return 'action'; // default
}

/**
 * Estrae il messaggio da un nodo Botpress
 */
function extractMessage(bpNode: BotpressNode): string {
  if (!bpNode.instructions) return '';
  
  for (const instr of bpNode.instructions) {
    if (instr.type === 'content' && instr.content?.text) {
      // Prova staticValue prima, poi dynamicValue
      return instr.content.text.staticValue || instr.content.text.dynamicValue || '';
    }
  }
  
  return bpNode.comment || '';
}

/**
 * Estrae tutte le informazioni utili da un nodo Botpress
 */
function extractNodeData(bpNode: BotpressNode): any {
  const data: any = {
    label: bpNode.name || '',
    message: extractMessage(bpNode),
    originalType: bpNode.type,
  };
  
  // Estrai variabili se esistono
  if ((bpNode as any).variables) {
    data.variables = (bpNode as any).variables;
  }
  
  // Estrai istruzioni utili
  if (bpNode.instructions) {
    // Action (codice JavaScript)
    const actionInstr = bpNode.instructions.find((i: any) => i.type === 'action');
    if (actionInstr) {
      data.action = {
        code: actionInstr.code || '',
        label: actionInstr.label || '',
        origin: actionInstr.origin || '',
        transpiledCode: actionInstr.transpiledCode || '',
      };
    }
    
    // Capture (input avanzato con KB)
    const captureInstr = bpNode.instructions.find((i: any) => i.type === 'capture');
    if (captureInstr) {
      data.capture = {
        entityId: captureInstr.entityId || '',
        entityName: captureInstr.entityName || '',
        variableId: captureInstr.variableId || '',
        question: captureInstr.question?.dynamicValue || captureInstr.question?.staticValue || '',
        kbPriority: captureInstr.kbPriority,
        retry: captureInstr.retry,
        prompt: captureInstr.prompt,
      };
    }
    
    // KB Answer / RAG
    const kbInstr = bpNode.instructions.find((i: any) => i.type === 'kb_answer');
    if (kbInstr) {
      data.knowledgeBase = kbInstr;
    }
    
    // Listen / Input
    const listenInstr = bpNode.instructions.find((i: any) => i.type === 'listen');
    if (listenInstr) {
      data.inputConfig = listenInstr;
    }
    
    // Conditions
    const conditions = bpNode.instructions.filter((i: any) => i.type === 'transition' && i.condition);
    if (conditions.length > 0) {
      data.conditions = conditions.map((c: any) => ({
        label: c.label || 'always',
        expression: c.condition?.payload || 'true',
        target: c.destination?.node,
      }));
    }
  }
  
  return data;
}

/**
 * Converte un flow Botpress in nodi e edges PIGNA
 */
function convertFlow(flow: BotpressFlow, flowIndex: number): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  
  // Offset per separare visivamente i flow multipli
  const xOffset = flowIndex * 600;
  
  // Converti nodi
  flow.nodes.forEach((bpNode) => {
    const pignType = mapNodeType(bpNode);
    
    // Salta nodi comment per ora (o potremmo aggiungerli come annotazioni)
    if (bpNode.type === 'comment') return;
    
    const node: Node = {
      id: bpNode.id,
      type: pignType,
      position: { 
        x: (bpNode.x || 0) + xOffset, 
        y: bpNode.y || 0 
      },
      data: extractNodeData(bpNode),
    };
    
    nodes.push(node);
  });
  
  // Converti edges (links) - con debug
  console.log(`[Botpress Import] Flow "${flow.name}": ${flow.nodes.length} nodi, ${flow.links?.length || 0} links espliciti`);
  console.log(`[Botpress Import] Nodi importati (dopo filtro):`, nodes.map(n => n.id));
  console.log(`[Botpress Import] Raw flow.links:`, flow.links);
  
  if (flow.links && flow.links.length > 0) {
    flow.links.forEach((link) => {
      // Verifica che source e target esistano nei nodi importati
      const sourceExists = nodes.find(n => n.id === link.source);
      const targetExists = nodes.find(n => n.id === link.target);
      
      if (sourceExists && targetExists) {
        const edge: Edge = {
          id: link.id || `link-${link.source}-${link.target}`,
          source: link.source,
          target: link.target,
          animated: true,
        };
        
        edges.push(edge);
        console.log(`[Botpress Import] ✅ Link aggiunto: ${link.source} → ${link.target}`);
      } else {
        console.warn(`[Botpress Import] ⚠️ Link ignorato (nodi mancanti): ${link.source} → ${link.target}`);
      }
    });
  }
  
  // Aggiungi edge da defaultTransition se non già presente
  console.log(`[Botpress Import] Controllo defaultTransition per ${flow.nodes.length} nodi...`);
  
  flow.nodes.forEach((bpNode) => {
    if (bpNode.defaultTransition?.targetNodeId) {
      console.log(`[Botpress Import] defaultTransition trovata: ${bpNode.id} → ${bpNode.defaultTransition.targetNodeId}`);
      const targetExists = nodes.find(n => n.id === bpNode.defaultTransition!.targetNodeId);
      
      if (!targetExists) {
        console.warn(`[Botpress Import] ⚠️ defaultTransition ignorata: ${bpNode.id} → ${bpNode.defaultTransition.targetNodeId} (target mancante)`);
        return;
      }
      
      const existingEdge = edges.find(e => 
        e.source === bpNode.id && e.target === bpNode.defaultTransition!.targetNodeId
      );
      
      if (!existingEdge) {
        edges.push({
          id: `default-${bpNode.id}`,
          source: bpNode.id,
          target: bpNode.defaultTransition.targetNodeId,
          animated: false,
          style: { strokeDasharray: '5,5' }, // dashed per default transition
        });
        console.log(`[Botpress Import] ✅ defaultTransition aggiunta: ${bpNode.id} → ${bpNode.defaultTransition.targetNodeId}`);
      }
    }
    
    // Aggiungi edge da "transitions" (formato Botpress v12+)
    if (bpNode.transitions && bpNode.transitions.length > 0) {
      console.log(`[Botpress Import] transitions trovate (${bpNode.transitions.length}) per ${bpNode.id}`);
      
      bpNode.transitions.forEach((trans, idx) => {
        const targetExists = nodes.find(n => n.id === trans.target);
        
        if (!targetExists) {
          console.warn(`[Botpress Import] ⚠️ transition ignorata: ${bpNode.id} → ${trans.target} (target mancante)`);
          return;
        }
        
        const existingEdge = edges.find(e => 
          e.source === bpNode.id && e.target === trans.target
        );
        
        if (!existingEdge) {
          edges.push({
            id: `trans-${bpNode.id}-${idx}`,
            source: bpNode.id,
            target: trans.target,
            label: trans.label || trans.condition || undefined,
            animated: true,
          });
          console.log(`[Botpress Import] ✅ transition aggiunta: ${bpNode.id} → ${trans.target}`);
        }
      });
    }
    
    // Aggiungi edge da "next" (formato alternativo)
    if (bpNode.next && bpNode.next.length > 0) {
      console.log(`[Botpress Import] next trovati (${bpNode.next.length}) per ${bpNode.id}`);
      
      bpNode.next.forEach((nextNode, idx) => {
        const targetExists = nodes.find(n => n.id === nextNode.node);
        
        if (!targetExists) {
          console.warn(`[Botpress Import] ⚠️ next ignorato: ${bpNode.id} → ${nextNode.node} (target mancante)`);
          return;
        }
        
        const existingEdge = edges.find(e => 
          e.source === bpNode.id && e.target === nextNode.node
        );
        
        if (!existingEdge) {
          edges.push({
            id: `next-${bpNode.id}-${idx}`,
            source: bpNode.id,
            target: nextNode.node,
            animated: true,
          });
          console.log(`[Botpress Import] ✅ next aggiunto: ${bpNode.id} → ${nextNode.node}`);
        }
      });
    }
  });
  
  return { nodes, edges };
}

export interface BotpressImportResult {
  nodes: Node[];
  edges: Edge[];
  flowNames: string[];
  botSettings?: {
    version: string;
    botId: string;
    languages: string[];
    inactivityTimeout: number;
    nodeRepetitionLimit: number;
    defaultBestModel?: string;
    defaultFastModel?: string;
    botVariables: any[];
    userVariables: any[];
  };
  flowVariables?: Record<string, any[]>;
  intents?: any[];
  knowledgeBase?: any[];
  documents?: any[];
  cloudFiles?: any[];
}

/**
 * Importa un file .bpz (ZIP) o bot.json
 */
export async function importBotpressFile(file: File): Promise<BotpressImportResult> {
  const fileName = file.name.toLowerCase();
  
  let botJson: BotpressBot;
  let documentsJson: any[] = [];
  let cloudFilesJson: any[] = [];
  
  // Se è un file .bpz (ZIP)
  if (fileName.endsWith('.bpz') || fileName.endsWith('.zip')) {
    try {
      const zip = await JSZip.loadAsync(file);
      
      // Leggi bot.json
      const botFile = zip.file('bot.json');
      if (!botFile) {
        throw new Error('File bot.json non trovato nello ZIP');
      }
      const botContent = await botFile.async('text');
      botJson = JSON.parse(botContent);
      
      // Leggi documents.json (opzionale)
      const docsFile = zip.file('documents.json');
      if (docsFile) {
        const docsContent = await docsFile.async('text');
        documentsJson = JSON.parse(docsContent);
      }
      
      // Leggi cloud_files.json (opzionale)
      const cloudFile = zip.file('cloud_files.json');
      if (cloudFile) {
        const cloudContent = await cloudFile.async('text');
        cloudFilesJson = JSON.parse(cloudContent);
      }
    } catch (error) {
      throw new Error(`Errore lettura .bpz: ${error}`);
    }
  }
  // Se è un file JSON diretto
  else if (fileName.endsWith('.json')) {
    try {
      const content = await file.text();
      botJson = JSON.parse(content);
    } catch (error) {
      throw new Error(`Errore lettura JSON: ${error}`);
    }
  }
  else {
    throw new Error('Formato file non supportato. Usa .bpz o .json');
  }
  
  // Converti tutti i flow (escludi Error, Timeout, Conversation End)
  const allNodes: Node[] = [];
  const allEdges: Edge[] = [];
  const flowNames: string[] = [];
  const flowVariables: Record<string, any[]> = {};
  
  const excludedFlows = ['error', 'timeout', 'conversation end'];
  
  botJson.flows.forEach((flow, index) => {
    // Salta flow di sistema
    if (excludedFlows.includes(flow.name.toLowerCase())) {
      return;
    }
    
    const { nodes, edges } = convertFlow(flow, index);
    allNodes.push(...nodes);
    allEdges.push(...edges);
    flowNames.push(flow.name);
    
    // Salva variabili del flow
    if (flow.variables && flow.variables.length > 0) {
      flowVariables[flow.name] = flow.variables;
    }
  });
  
  // Estrai bot settings
  const botSettings = botJson.settings ? {
    version: botJson.version || '',
    botId: botJson.settings.id || '',
    languages: botJson.settings.languages || [],
    inactivityTimeout: botJson.settings.inactivityTimeout || 30,
    nodeRepetitionLimit: botJson.settings.nodeRepetitionLimit || 3,
    defaultBestModel: botJson.settings.defaultBestModel,
    defaultFastModel: botJson.settings.defaultFastModel,
    botVariables: botJson.settings.botVariables || [],
    userVariables: botJson.settings.userVariables || [],
  } : undefined;
  
  console.log(`[Botpress Import] ✅ Import completato:`);
  console.log(`  - ${allNodes.length} nodi totali`);
  console.log(`  - ${allEdges.length} collegamenti totali`);
  console.log(`  - ${flowNames.length} flow importati: ${flowNames.join(', ')}`);
  
  if (allEdges.length === 0 && allNodes.length > 0) {
    console.warn(`[Botpress Import] ⚠️ ATTENZIONE: Importati ${allNodes.length} nodi ma 0 collegamenti!`);
    console.warn(`  → Verifica che il bot.json contenga "links", "defaultTransition", "transitions" o "next" nei nodi.`);
    console.warn(`  → Dump del primo nodo per analisi:`, botJson.flows[0]?.nodes[0]);
  }
  
  return {
    nodes: allNodes,
    edges: allEdges,
    flowNames,
    botSettings,
    flowVariables: Object.keys(flowVariables).length > 0 ? flowVariables : undefined,
    intents: (botJson as any).intents || [],
    knowledgeBase: (botJson as any).knowledge_base || [],
    documents: documentsJson.length > 0 ? documentsJson : undefined,
    cloudFiles: cloudFilesJson.length > 0 ? cloudFilesJson : undefined,
  };
}

